import {
  addMinutes,
  addMonths,
  addYears,
  differenceInCalendarMonths,
  differenceInDays,
  differenceInSeconds,
  format,
  isAfter,
  isBefore,
  isValid,
  parse,
} from "date-fns";

import {
  FULL_DATE_FORMAT,
  TIME_FORMAT,
  TIME_WITHOUT_SECONDS_FORMAT,
} from "../constants";

export class DateHelper {
  public static elapsedSeconds(
    start: Date | string,
    end: Date | string
  ): number {
    const seconds = differenceInSeconds(new Date(end), new Date(start));

    return seconds === 0 ? 0 : seconds;
  }

  // Gets the browser timezone offset in hours.
  public static getTimezoneOffsetInHours(): number {
    return new Date().getTimezoneOffset() / 60;
  }

  // Creates a date from its UTC values.
  public static formatDateInUTC(date: Date | string): Date {
    const initialDate = new Date(date);

    return new Date(
      initialDate.getUTCFullYear(),
      initialDate.getUTCMonth(),
      initialDate.getUTCDate(),
      initialDate.getUTCHours(),
      initialDate.getUTCMinutes(),
      initialDate.getUTCSeconds(),
      initialDate.getUTCMilliseconds()
    );
  }

  // Converts a date to a UTC YYYY-MM-DD string.
  public static toUtcCalendarDay(
    date?: Date | string | null
  ): string | undefined {
    if (!date) {
      return undefined;
    }

    const value = new Date(date);

    if (Number.isNaN(value.getTime())) {
      return undefined;
    }

    return [
      value.getUTCFullYear(),
      String(value.getUTCMonth() + 1).padStart(2, "0"),
      String(value.getUTCDate()).padStart(2, "0"),
    ].join("-");
  }

  // Creates a UTC date from a YYYY-MM-DD string.
  public static fromUtcCalendarDay(value: string): Date {
    const [year, month, day] = value.split("-").map(Number);

    return new Date(Date.UTC(year, month - 1, day));
  }

  // Formats a date in UTC using the supplied locale or browser language.
  public static formatInUTC(
    date: Date | string,
    options: Intl.DateTimeFormatOptions,
    locale?: string | string[]
  ): string {
    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: "UTC",
    }).format(new Date(date));
  }

  // Normalizes values emitted by the shared date picker.
  public static parsePickerDate(value: unknown): Date | null {
    if (value instanceof Date) return isValid(value) ? value : null;

    if (typeof value === "string") {
      const parsed = parse(value, FULL_DATE_FORMAT, new Date());

      return isValid(parsed) ? parsed : null;
    }

    return null;
  }

  public static getUtcDayStart(date: Date): Date {
    return new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    );
  }

  // Gets the first day of a UTC month.
  public static getUtcMonthStart(date: Date, monthOffset = 0): Date {
    return new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthOffset, 1)
    );
  }

  // Gets the Sunday at the start of a UTC week.
  public static getUtcWeekStart(date: Date): Date {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate() - date.getUTCDay()
      )
    );
  }

  // Returns a new date moved by UTC days.
  public static addUtcDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);

    return result;
  }

  // Converts local date values to UTC.
  public static convertLocalTimeToUTC(date: Date | string): Date {
    const initialDate = new Date(date);

    return new Date(
      Date.UTC(
        initialDate.getFullYear(),
        initialDate.getMonth(),
        initialDate.getDate(),
        initialDate.getHours(),
        initialDate.getMinutes(),
        initialDate.getSeconds(),
        initialDate.getMilliseconds()
      )
    );
  }

  // Formats a UTC date with time.
  public static fullDateAndTimeFormatInUTC(date: Date | string): string {
    return format(
      DateHelper.formatDateInUTC(date),
      `${FULL_DATE_FORMAT} ${TIME_FORMAT}`
    );
  }

  // Formats a UTC date with time but no seconds.
  public static fullDateAndTimeWithoutSecondsFormatInUTC(
    date: Date | string
  ): string {
    return format(
      DateHelper.formatDateInUTC(date),
      `${FULL_DATE_FORMAT} ${TIME_WITHOUT_SECONDS_FORMAT}`
    );
  }

  // Formats a UTC date.
  public static fullDateFormatInUTC(date: Date | string): string {
    return format(DateHelper.formatDateInUTC(date), FULL_DATE_FORMAT);
  }

  // Gets the latest date used in filters.
  public static getMaxDate(): Date {
    return new Date(86400000000000);
  }

  // Gets the earliest date used in filters.
  public static getMinDate(): Date {
    return new Date(0);
  }

  // Calculates age in years.
  public static getAge(birthday: Date): number {
    const today = new Date();
    let age = today.getFullYear() - birthday.getFullYear();
    const monthDifference = today.getMonth() - birthday.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthday.getDate())
    ) {
      age--;
    }

    return age;
  }

  // Shows the time between two dates.
  public static getDifferenceBetweenDates(
    startDate: Date | string,
    endDate: Date | string = new Date()
  ): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const years = Math.trunc(DateHelper.wholeMonthsBetween(start, end) / 12);
    const afterYears = addYears(start, years);
    const months = DateHelper.wholeMonthsBetween(afterYears, end);
    const days = differenceInDays(end, addMonths(afterYears, months));

    return `${years ? years + " year(s) " : ""}${
      months ? months + " month(s) " : ""
    }${days ? days + " day(s)" : ""}`;
  }

  private static wholeMonthsBetween(start: Date, end: Date): number {
    // Anchor on the larger day of the month so clamped month ends work in both directions.
    if (start.getDate() < end.getDate())
      return -DateHelper.wholeMonthsBetween(end, start);

    const months = differenceInCalendarMonths(end, start);
    const anchor = addMonths(start, months);

    if (months > 0 && isAfter(anchor, end)) return months - 1;

    if (months < 0 && isBefore(anchor, end)) return months + 1;

    return months;
  }

  public static addMinutes(date: Date | string, minutes: number): Date {
    return addMinutes(new Date(date), minutes);
  }

  // Gets the first day of a local month.
  public static getFirstDateOfMonth(date = new Date()): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  // Gets the last day of a local month.
  public static getLastDateOfMonth(date = new Date()): Date {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }

  // Moves a date by UTC months.
  public static addMonthsToDate(date: Date, months: number): Date {
    const newDate = new Date(date);
    const day = newDate.getUTCDate();

    newDate.setUTCDate(1);
    newDate.setUTCMonth(newDate.getUTCMonth() + months + 1);
    newDate.setUTCDate(0);

    const lastDay = newDate.getUTCDate();
    newDate.setUTCDate(day > lastDay ? lastDay : day);

    return newDate;
  }
}
